import {
  AccountBalanceWallet,
  ArrowDownward,
  ArrowUpward,
  Cancel,
  PersonAdd,
  PersonOff,
  Save,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import LogoLoader from "../components/common/LogoLoader";
import Sidebar from "../components/Layout/Sidebar";
import { useAuth } from "../contexts/AuthContext";
import {
  billingApi,
  CreditBalance as CreditBalanceType,
  CreditSummary,
  UsageHistoryEntry,
} from "../services/billingApi";
import {
  OrganizationDetail,
  OrganizationMember,
  PendingInvitation,
  settingsApi,
} from "../services/settingsApi";

// ─── Profile Tab ────────────────────────────────────────────────────────────

const ProfileTab: React.FC = () => {
  const { user, refreshUser } = useAuth();

  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [lastName, setLastName] = useState(user?.last_name ?? "");
  const [jobTitle, setJobTitle] = useState(user?.profile?.job_title ?? "");
  const [department, setDepartment] = useState(user?.profile?.department ?? "");
  const [phone, setPhone] = useState(user?.profile?.phone ?? "");

  const profileDirty =
    firstName !== (user?.first_name ?? "") ||
    lastName !== (user?.last_name ?? "") ||
    jobTitle !== (user?.profile?.job_title ?? "") ||
    department !== (user?.profile?.department ?? "") ||
    phone !== (user?.profile?.phone ?? "");

  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState("");
  const [profileError, setProfileError] = useState("");

  // Password change
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError("");
    setProfileSuccess("");
    setProfileSaving(true);
    try {
      await settingsApi.updateProfile({
        first_name: firstName,
        last_name: lastName,
        job_title: jobTitle,
        department,
        phone,
      });
      await refreshUser();
      setProfileSuccess("Profile updated successfully.");
    } catch (err: any) {
      setProfileError(
        err.response?.data?.error || err.message || "Failed to update profile.",
      );
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (newPassword !== newPasswordConfirm) {
      setPasswordError("Passwords don't match.");
      return;
    }

    setPasswordSaving(true);
    try {
      await settingsApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      });
      setPasswordSuccess("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordConfirm("");
      setShowPasswordSection(false);
    } catch (err: any) {
      const data = err.response?.data;
      const msg =
        data?.error ||
        data?.new_password?.join(" ") ||
        data?.new_password_confirm?.join(" ") ||
        err.message ||
        "Failed to change password.";
      setPasswordError(msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Box>
      {/* Read-only info */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Email
        </Typography>
        <Typography variant="body1">{user?.email}</Typography>
      </Box>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Account Status
          </Typography>
          <Chip
            label={user?.account_status?.replace("_", " ") ?? "Unknown"}
            color={
              user?.account_status === "FULL_ACCESS" ||
              user?.account_status === "STAFF"
                ? "success"
                : user?.account_status === "TRIAL"
                  ? "warning"
                  : "default"
            }
            size="small"
          />
        </Box>
        {user?.organization_role && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Organization Role
            </Typography>
            <Chip
              label={
                user.organization_role === "owner"
                  ? "Owner"
                  : user.organization_role === "admin"
                    ? "Admin"
                    : "Member"
              }
              color={
                user.organization_role === "owner"
                  ? "secondary"
                  : user.organization_role === "admin"
                    ? "primary"
                    : "default"
              }
              size="small"
            />
          </Box>
        )}
        {user?.access_expires_at && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Access Expires
            </Typography>
            <Typography variant="body1">
              {new Date(user.access_expires_at).toLocaleDateString()}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Editable profile form */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Edit Profile
      </Typography>

      {profileSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setProfileSuccess("")}
        >
          {profileSuccess}
        </Alert>
      )}
      {profileError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setProfileError("")}
        >
          {profileError}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleProfileSave}
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 500 }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            size="small"
          />
        </Box>
        <TextField
          label="Job Title"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          fullWidth
          size="small"
        />
        <TextField
          label="Department"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          fullWidth
          size="small"
        />
        <TextField
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
          size="small"
        />
        <Box>
          <Button
            type="submit"
            variant="contained"
            startIcon={
              profileSaving ? <CircularProgress size={16} /> : <Save />
            }
            disabled={profileSaving || !profileDirty}
          >
            Save Profile
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Password change */}
      <Box sx={{ maxWidth: 500 }}>
        {!showPasswordSection ? (
          <Button
            variant="outlined"
            onClick={() => setShowPasswordSection(true)}
          >
            Change Password
          </Button>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Change Password
            </Typography>

            {passwordSuccess && (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                onClose={() => setPasswordSuccess("")}
              >
                {passwordSuccess}
              </Alert>
            )}
            {passwordError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setPasswordError("")}
              >
                {passwordError}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handlePasswordChange}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <TextField
                label="Current Password"
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {showCurrentPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {showNewPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm New Password"
                type="password"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                required
                size="small"
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={passwordSaving}
                  startIcon={
                    passwordSaving ? <CircularProgress size={16} /> : undefined
                  }
                >
                  Update Password
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setShowPasswordSection(false);
                    setPasswordError("");
                    setPasswordSuccess("");
                    setCurrentPassword("");
                    setNewPassword("");
                    setNewPasswordConfirm("");
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

// ─── Organization Tab ───────────────────────────────────────────────────────

const OrganizationTab: React.FC = () => {
  const { user } = useAuth();

  const [org, setOrg] = useState<OrganizationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Rename
  const [orgName, setOrgName] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);

  // Invite
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSaving, setInviteSaving] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    onConfirm: () => void;
  }>({ open: false, title: "", message: "", onConfirm: () => {} });

  const fetchOrg = useCallback(async () => {
    try {
      const data = await settingsApi.getOrganization();
      setOrg(data);
      setOrgName(data.name);
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to load organization details.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  const handleRename = async () => {
    if (!orgName.trim()) return;
    setRenameSaving(true);
    setError("");
    try {
      const data = await settingsApi.renameOrganization(orgName.trim());
      setOrg(data);
      setOrgName(data.name);
      setSuccess("Organization renamed successfully.");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to rename organization.");
    } finally {
      setRenameSaving(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = await settingsApi.inviteMember(inviteEmail.trim());
      setSuccess(response.message);
      setInviteEmail("");
      await fetchOrg();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to invite member.");
    } finally {
      setInviteSaving(false);
    }
  };

  const handleToggleStatus = (member: OrganizationMember) => {
    const isDeactivated = member.account_status === "DEACTIVATED";
    const displayName =
      `${member.first_name} ${member.last_name}`.trim() || member.username;
    setConfirmDialog({
      open: true,
      title: isDeactivated ? "Reactivate User" : "Deactivate User",
      message: isDeactivated
        ? `Are you sure you want to reactivate ${displayName} (${member.email})?`
        : `Are you sure you want to deactivate ${displayName} (${member.email})? They will lose access to the platform.`,
      confirmLabel: isDeactivated ? "Reactivate" : "Deactivate",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        setError("");
        setSuccess("");
        try {
          const response = await settingsApi.toggleMemberStatus(member.id);
          setSuccess(response.message);
          await fetchOrg();
        } catch (err: any) {
          setError(
            err.response?.data?.error || "Failed to update member status.",
          );
        }
      },
    });
  };

  const handleToggleRole = (member: OrganizationMember) => {
    const newIsAdmin = !member.is_admin;
    const displayName =
      `${member.first_name} ${member.last_name}`.trim() || member.username;
    const action = newIsAdmin
      ? `promote ${displayName} to admin`
      : `demote ${displayName} to member`;
    setConfirmDialog({
      open: true,
      title: newIsAdmin ? "Promote to Admin" : "Demote to Member",
      message: `Are you sure you want to ${action}?`,
      confirmLabel: "Confirm",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        setError("");
        setSuccess("");
        try {
          await settingsApi.changeMemberRole(member.id, newIsAdmin);
          setSuccess(
            `${displayName} is now ${newIsAdmin ? "an admin" : "a member"}.`,
          );
          await fetchOrg();
        } catch (err: any) {
          setError(
            err.response?.data?.error || "Failed to update member role.",
          );
        }
      },
    });
  };

  const handleCancelInvite = (invitation: PendingInvitation) => {
    setConfirmDialog({
      open: true,
      title: "Cancel Invitation",
      message: `Are you sure you want to cancel the invitation for ${invitation.email}?`,
      confirmLabel: "Cancel Invitation",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        setError("");
        setSuccess("");
        try {
          await settingsApi.cancelInvitation(invitation.id);
          setSuccess("Invitation cancelled.");
          await fetchOrg();
        } catch (err: any) {
          setError(err.response?.data?.error || "Failed to cancel invitation.");
        }
      },
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <LogoLoader size="section" label="Loading the organisation" />
      </Box>
    );
  }

  if (!org) {
    return (
      <Alert severity="info">You are not a member of any organization.</Alert>
    );
  }

  return (
    <Box>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Organization name */}
      <Box
        component="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          if (orgName.trim() !== org.name) handleRename();
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          maxWidth: 500,
          mb: 3,
        }}
      >
        <TextField
          label="Organization Name"
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          size="small"
          fullWidth
          disabled={!org.is_admin}
        />
        {org.is_admin && (
          <Box>
            <Button
              type="submit"
              variant="contained"
              startIcon={
                renameSaving ? <CircularProgress size={16} /> : <Save />
              }
              disabled={
                renameSaving || !orgName.trim() || orgName.trim() === org.name
              }
            >
              Save Organization
            </Button>
          </Box>
        )}
      </Box>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Members ({org.member_count}
        {org.pending_invite_count > 0 &&
          ` + ${org.pending_invite_count} pending`}
        {org.max_users != null && ` / ${org.max_users} seats`})
      </Typography>

      {/* Members table */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ mb: 3, maxWidth: 800 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Joined</TableCell>
              {org.is_admin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {org.members.map((member) => {
              const isCurrentUser = member.id === user?.id;
              const displayName =
                `${member.first_name} ${member.last_name}`.trim() ||
                member.username;

              const isDeactivated = member.account_status === "DEACTIVATED";

              return (
                <TableRow
                  key={member.id}
                  sx={isDeactivated ? { opacity: 0.5 } : undefined}
                >
                  <TableCell>
                    {displayName}
                    {isCurrentUser && (
                      <Chip
                        label="You"
                        size="small"
                        sx={{ ml: 1 }}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>
                    {isDeactivated ? (
                      <Chip label="Deactivated" size="small" color="error" />
                    ) : (
                      <Chip
                        label={
                          member.is_owner
                            ? "Owner"
                            : member.is_admin
                              ? "Admin"
                              : "Member"
                        }
                        size="small"
                        color={
                          member.is_owner
                            ? "secondary"
                            : member.is_admin
                              ? "primary"
                              : "default"
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(member.date_joined).toLocaleDateString()}
                  </TableCell>
                  {org.is_admin && (
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        {!member.is_owner && (
                          <Tooltip
                            title={
                              member.is_admin
                                ? "Demote to member"
                                : "Promote to admin"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleToggleRole(member)}
                            >
                              {member.is_admin ? (
                                <ArrowDownward
                                  fontSize="small"
                                  color="warning"
                                />
                              ) : (
                                <ArrowUpward fontSize="small" color="primary" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isCurrentUser && !member.is_owner && (
                          <Tooltip
                            title={
                              member.account_status === "DEACTIVATED"
                                ? "Reactivate user"
                                : "Deactivate user"
                            }
                          >
                            <IconButton
                              size="small"
                              color={
                                member.account_status === "DEACTIVATED"
                                  ? "success"
                                  : "error"
                              }
                              onClick={() => handleToggleStatus(member)}
                            >
                              {member.account_status === "DEACTIVATED" ? (
                                <PersonAdd fontSize="small" />
                              ) : (
                                <PersonOff fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {org.pending_invitations.map((invitation) => (
              <TableRow key={`invite-${invitation.id}`} sx={{ opacity: 0.7 }}>
                <TableCell sx={{ fontStyle: "italic" }}>—</TableCell>
                <TableCell>{invitation.email}</TableCell>
                <TableCell>
                  <Chip label="Pending" size="small" color="warning" />
                </TableCell>
                <TableCell>
                  {new Date(invitation.created).toLocaleDateString()}
                </TableCell>
                {org.is_admin && (
                  <TableCell align="right">
                    <Tooltip title="Cancel invitation">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleCancelInvite(invitation)}
                      >
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Invite section (admin only) */}
      {org.is_admin && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Invite Member
          </Typography>
          {!org.can_add_members ? (
            <Alert severity="warning" sx={{ maxWidth: 600 }}>
              Your organization has reached its member limit ({org.max_users}{" "}
              seats). Contact support to increase your plan.
            </Alert>
          ) : (
            <Box
              component="form"
              onSubmit={handleInvite}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                maxWidth: 600,
              }}
            >
              <TextField
                label="Email address"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                size="small"
                sx={{ flex: 1 }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={inviteSaving}
                startIcon={
                  inviteSaving ? <CircularProgress size={16} /> : undefined
                }
              >
                Invite
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Confirm dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        PaperProps={{
          sx: { backgroundColor: "background.paper" },
        }}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog((prev) => ({ ...prev, open: false }))
            }
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDialog.onConfirm}
            color="error"
            variant="contained"
          >
            {confirmDialog.confirmLabel || "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Billing Tab ───────────────────────────────────────────────────────────

const SummaryCard: React.FC<{ label: string; value: string; color?: string }> = ({
  label,
  value,
  color = "text.primary",
}) => (
  <Paper
    variant="outlined"
    sx={{
      flex: 1,
      p: 2.5,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.5,
    }}
  >
    <Typography variant="h4" sx={{ fontWeight: 700, color }}>
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Paper>
);

const BillingTab: React.FC = () => {
  const [summary, setSummary] = useState<CreditSummary | null>(null);
  const [balances, setBalances] = useState<CreditBalanceType[]>([]);
  const [usageHistory, setUsageHistory] = useState<UsageHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [summaryData, balancesData, historyData] = await Promise.all([
        billingApi.getSummary(),
        billingApi.getBalances(),
        billingApi.getUsageHistory(30),
      ]);
      setSummary(summaryData);
      setBalances(
        [...balancesData].sort(
          (a, b) =>
            new Date(b.valid_from).getTime() - new Date(a.valid_from).getTime(),
        ),
      );
      setUsageHistory(historyData);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load billing data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <LogoLoader size="panel" label="Loading the settings" />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const remaining = parseFloat(summary?.total_available || "0");
  const granted = parseFloat(summary?.total_granted || "0");
  const consumed = parseFloat(summary?.total_consumed || "0");

  const getBalanceStatus = (b: CreditBalanceType) => {
    if (!b.is_active) return { label: "Invalidated", color: "default" as const };
    if (b.is_expired) return { label: "Expired", color: "error" as const };
    if (parseFloat(b.remaining) <= 0) return { label: "Exhausted", color: "warning" as const };
    return { label: "Active", color: "success" as const };
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Summary Cards */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <SummaryCard
          label="Credits Remaining"
          value={remaining.toFixed(1)}
          color="success.main"
        />
        <SummaryCard
          label="Credits Used"
          value={consumed.toFixed(1)}
        />
        <SummaryCard
          label="Credits Granted"
          value={granted.toFixed(1)}
        />
      </Box>

      {/* Usage Chart */}
      {usageHistory.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Usage (last 30 days)
          </Typography>
          <Box sx={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={usageHistory}
                margin={{ top: 4, right: 16, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="grad-usage" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1976d2" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#1976d2" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.04)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#616161" }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.ceil(usageHistory.length / 7) - 1}
                  tickFormatter={(v) => {
                    const d = new Date(String(v));
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#616161" }}
                  tickLine={false}
                  axisLine={false}
                />
                <RechartTooltip
                  contentStyle={{
                    backgroundColor: "#1e1e1e",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={(v) => new Date(String(v)).toLocaleDateString()}
                  formatter={(value) => [Number(value).toFixed(2), "Credits"]}
                  cursor={{ stroke: "rgba(255,255,255,0.1)", strokeWidth: 1 }}
                />
                <Area
                  type="monotone"
                  dataKey="credits"
                  name="Credits Used"
                  stroke="#1976d2"
                  strokeWidth={1.5}
                  fill="url(#grad-usage)"
                  dot={false}
                  activeDot={{ r: 3, fill: "#1976d2", strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}

      {/* Credit Balances Table */}
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Credit Balances
        </Typography>
        {balances.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No credit balances found.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="right">Granted</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell>Valid From</TableCell>
                  <TableCell>Valid Until</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balances.map((b) => {
                  const status = getBalanceStatus(b);
                  return (
                    <TableRow key={b.id}>
                      <TableCell align="right">
                        {parseFloat(b.original_volume).toFixed(1)}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(b.remaining).toFixed(1)}
                      </TableCell>
                      <TableCell>
                        {new Date(b.valid_from).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(b.valid_until).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

// ─── Settings Page ──────────────────────────────────────────────────────────

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [isOrgMember, setIsOrgMember] = useState(false);

  // Check org membership for billing tab visibility
  useEffect(() => {
    settingsApi
      .getOrganization()
      .then(() => setIsOrgMember(true))
      .catch(() => setIsOrgMember(false));
  }, [user]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        backgroundColor: "background.paper",
      }}
    >
      <Sidebar />

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 800,
            mx: "auto",
            pt: 4,
            pb: 4,
            px: 3,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
            Settings
          </Typography>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Profile" />
            <Tab label="Organization" />
            {isOrgMember && <Tab label="Billing" />}
          </Tabs>

          {tab === 0 && <ProfileTab />}
          {tab === 1 && <OrganizationTab />}
          {tab === 2 && isOrgMember && <BillingTab />}
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;
